import type {
  OperatorIncidentSnapshot,
  OperatorOverview,
  OperatorPipelineCard,
  OperatorRecapEntry,
  OperatorRecapV1,
  OperatorReportRow,
  OperatorSiteSnapshot,
  RecapEntryKind,
  SeoAuditRunId,
  SiteId,
} from "@siteagain/shared-types";

export type Mc2Tone = "neutral" | "up" | "down" | "warn" | "info" | "lime" | "ink";

export const CHIP: Record<Mc2Tone, string> = {
  neutral: "mc2-chip",
  up: "mc2-chip up",
  down: "mc2-chip down",
  warn: "mc2-chip warn",
  info: "mc2-chip info",
  lime: "mc2-chip lime",
  ink: "mc2-chip ink",
};

export function toneForVerdict(verdict: string | null): { tone: Mc2Tone; mark: string } {
  if (verdict === "improved") return { tone: "up", mark: "+" };
  if (verdict === "declined") return { tone: "down", mark: "−" };
  if (verdict === "insufficient") return { tone: "warn", mark: "?" };
  return { tone: "neutral", mark: "=" };
}

const STAGE_LABEL: Record<"en" | "fr", Record<string, string>> = {
  en: { active: "Live", preview_ready: "Preview ready", migrating: "Migrating", pending: "Pending", failed: "Failed", archived: "Archived" },
  fr: { active: "En ligne", preview_ready: "Aperçu prêt", migrating: "Migration", pending: "En attente", failed: "Échec", archived: "Archivé" },
};

export function siteStage(site: OperatorSiteSnapshot, locale: "en" | "fr" = "en"): { label: string; tone: Mc2Tone } {
  const label = STAGE_LABEL[locale][site.status] ?? site.status;
  switch (site.status) {
    case "active": return { label, tone: "ink" };
    case "preview_ready": return { label, tone: "info" };
    case "migrating": return { label, tone: "warn" };
    case "pending": return { label, tone: "warn" };
    case "failed": return { label, tone: "down" };
    case "archived": return { label, tone: "neutral" };
    default: return { label, tone: "neutral" };
  }
}

export function healthTone(site: OperatorSiteSnapshot): { label: string; dot: string } {
  switch (site.health) {
    case "healthy": return { label: "Healthy", dot: "ok" };
    case "building": return { label: "Building", dot: "warn" };
    case "watch": return { label: "Watch", dot: "warn" };
    case "critical": return { label: "Critical", dot: "bad" };
    default: return { label: site.health, dot: "idle" };
  }
}

/** The three setup facts the operator contract can actually prove. */
export function setupFacts(site: OperatorSiteSnapshot): Array<{ key: "audit" | "preview" | "live"; ok: boolean }> {
  const preview = site.latestMigration
    ? ["preview_ready", "awaiting_approval", "deploying", "completed"].includes(site.latestMigration.status)
    : false;
  const live = site.latestDeployment?.environment === "production" &&
    ["active", "publishing", "building"].includes(site.latestDeployment.status);
  return [
    { key: "audit", ok: site.latestSeoScore !== null },
    { key: "preview", ok: preview },
    { key: "live", ok: live || site.status === "active" },
  ];
}

export function averageScore(sites: readonly OperatorSiteSnapshot[]): { value: number | null; count: number } {
  const scores = sites.flatMap((site) => (site.latestSeoScore === null ? [] : [site.latestSeoScore]));
  if (scores.length === 0) return { value: null, count: 0 };
  return { value: scores.reduce((sum, score) => sum + score, 0) / scores.length, count: scores.length };
}

export function countBy<T>(items: readonly T[], pick: (item: T) => string | null): Map<string, number> {
  const map = new Map<string, number>();
  for (const item of items) {
    const key = pick(item);
    if (key) map.set(key, (map.get(key) ?? 0) + 1);
  }
  return map;
}

/** A 7-day bucket of the kinds of event a fleet operator scans for. */
export type HeatKind = "ship" | "audit" | "client" | "alert" | "none";
export interface HeatDay { iso: string; label: string; kind: HeatKind }

const HEAT_WEIGHT: Record<HeatKind, number> = { none: 0, client: 1, audit: 2, ship: 3, alert: 4 };

export function heatForJournal(
  sites: readonly OperatorSiteSnapshot[],
  journal: readonly OperatorRecapEntry[],
  reports: readonly OperatorReportRow[],
  now: number,
  locale: "en" | "fr" = "en",
): Array<{ siteId: SiteId; site: string; days: HeatDay[] }> {
  const days: Array<{ iso: string; label: string }> = [];
  for (let offset = 6; offset >= 0; offset -= 1) {
    const date = new Date(now - offset * 86_400_000);
    days.push({
      iso: date.toISOString().slice(0, 10),
      label: new Intl.DateTimeFormat(locale === "fr" ? "fr-FR" : "en-GB", { weekday: "short", day: "numeric" }).format(date),
    });
  }
  const kindOf = (kind: RecapEntryKind): HeatKind => {
    if (kind === "failed" || kind === "rolled_back" || kind === "audit_failed") return "alert";
    if (kind === "deployed" || kind === "measured") return "ship";
    if (kind === "preview_ready" || kind === "rejected" || kind === "audit_completed") return "audit";
    return "none";
  };
  return sites.slice(0, 10).map((site) => {
    const perDay = new Map<string, HeatKind>(days.map((day) => [day.iso, "none"]));
    for (const entry of journal) {
      if (entry.siteId !== site.id) continue;
      const iso = entry.at.slice(0, 10);
      if (!perDay.has(iso)) continue;
      const next = kindOf(entry.kind);
      const current = perDay.get(iso) ?? "none";
      if (HEAT_WEIGHT[next] > HEAT_WEIGHT[current]) perDay.set(iso, next);
    }
    for (const report of reports) {
      if (report.siteId !== site.id) continue;
      const stamp = report.reviewedAt ?? report.createdAt;
      const iso = stamp.slice(0, 10);
      if (!perDay.has(iso)) continue;
      if (HEAT_WEIGHT.client > HEAT_WEIGHT[perDay.get(iso) ?? "none"]) perDay.set(iso, "client");
    }
    return { siteId: site.id, site: site.name, days: days.map((day) => ({ ...day, kind: perDay.get(day.iso) ?? "none" })) };
  });
}

// ── Inbox ──────────────────────────────────────────────────────────────────

export interface InboxBase {
  id: string;
  at: string;
  siteId: SiteId;
  siteName: string;
  title: string;
  subtitle: string;
}

export type InboxItem =
  | (InboxBase & { kind: "stopped"; card: OperatorPipelineCard })
  | (InboxBase & { kind: "approve"; card: OperatorPipelineCard })
  | (InboxBase & { kind: "audit"; auditId: SeoAuditRunId; pending: number })
  | (InboxBase & { kind: "report"; report: OperatorReportRow })
  | (InboxBase & { kind: "setup"; site: OperatorSiteSnapshot })
  | (InboxBase & { kind: "incident"; incident: OperatorIncidentSnapshot });

export type InboxKind = InboxItem["kind"];

const INBOX_ORDER: Record<InboxKind, number> = {
  stopped: 0,
  incident: 1,
  approve: 2,
  setup: 3,
  audit: 4,
  report: 5,
};

export function buildInbox({
  stopped,
  toApprove,
  audits,
  sites,
  reports,
  incidents,
}: {
  stopped: readonly OperatorPipelineCard[];
  toApprove: readonly OperatorPipelineCard[];
  audits: ReadonlyArray<{ auditRunId: SeoAuditRunId; siteId: SiteId; siteName: string; at: string }>;
  sites: readonly OperatorSiteSnapshot[];
  reports: readonly OperatorReportRow[];
  incidents: readonly OperatorIncidentSnapshot[];
}): InboxItem[] {
  const pending = (siteId: SiteId) => sites.find((site) => site.id === siteId)?.pendingRecommendations ?? 0;
  const items: InboxItem[] = [];
  for (const card of stopped) {
    items.push({
      kind: "stopped", id: `stopped:${card.key}`, at: card.occurredAt, siteId: card.siteId, siteName: card.siteName,
      title: card.title, subtitle: card.errorMessage ?? "Stopped without a recorded message.", card,
    });
  }
  for (const card of toApprove) {
    items.push({
      kind: "approve", id: `approve:${card.key}`, at: card.occurredAt, siteId: card.siteId, siteName: card.siteName,
      title: card.title, subtitle: card.batch?.eligible ? "All checks passed · low risk" : "Preview ready · needs your decision", card,
    });
  }
  for (const audit of audits) {
    items.push({
      kind: "audit", id: `audit:${audit.auditRunId}`, at: audit.at, siteId: audit.siteId, siteName: audit.siteName,
      title: `Audit ready for ${audit.siteName}`, subtitle: pending(audit.siteId) > 0 ? `${pending(audit.siteId)} ${pending(audit.siteId) === 1 ? "recommendation" : "recommendations"} to decide` : "Open the report", auditId: audit.auditRunId, pending: pending(audit.siteId),
    });
  }
  for (const report of reports) {
    if (report.status !== "draft") continue;
    items.push({
      kind: "report", id: `report:${report.id}`, at: report.createdAt, siteId: report.siteId, siteName: report.siteName,
      title: `${report.month} update ready`, subtitle: report.quiet ? "Quiet month · stored, never emailed" : `${report.totals.shipped} shipped · ${report.totals.measured} measured`, report,
    });
  }
  for (const site of sites) {
    const stopped = site.latestMigration?.status === "failed";
    const blocked = (site.status === "pending" || site.status === "migrating" || site.status === "failed") &&
      (site.awaitingApproval > 0 || site.pendingRecommendations > 0 || stopped);
    if (!blocked) continue;
    const waiting = site.awaitingApproval + site.pendingRecommendations;
    items.push({
      kind: "setup", id: `setup:${site.id}`, at: site.lastActivityAt, siteId: site.id, siteName: site.name,
      title: `${site.name} cannot ship yet`,
      subtitle: waiting > 0
        ? `${waiting} ${waiting === 1 ? "change is" : "changes are"} waiting on one setup step`
        : "The migration stopped; open the site to see why",
      site,
    });
  }
  for (const incident of incidents) {
    items.push({
      kind: "incident", id: `incident:${incident.id}`, at: incident.createdAt, siteId: incident.siteId, siteName: incident.siteName,
      title: incident.summary, subtitle: `${incident.severity} · ${incident.issueType.replaceAll("_", " ")}`, incident,
    });
  }
  return items.sort((left, right) => {
    const order = INBOX_ORDER[left.kind] - INBOX_ORDER[right.kind];
    return order !== 0 ? order : right.at.localeCompare(left.at);
  });
}

export interface Mover {
  siteId: SiteId;
  siteName: string;
  mark: string;
  tone: Mc2Tone;
  why: string;
  at: string;
}

/** A measured or shipped result, with the change that caused it. Never a made-up score delta. */
export function buildMovers(journal: readonly OperatorRecapEntry[]): Mover[] {
  const bySite = new Map<SiteId, OperatorRecapEntry[]>();
  for (const entry of journal) {
    const list = bySite.get(entry.siteId) ?? [];
    list.push(entry);
    bySite.set(entry.siteId, list);
  }
  const movers: Mover[] = [];
  for (const [siteId, entries] of bySite) {
    const sorted = [...entries].sort((left, right) => right.at.localeCompare(left.at));
    const measured = sorted.find((entry) => entry.kind === "measured");
    const shipped = sorted.find((entry) => entry.kind === "deployed");
    const chosen = measured ?? shipped;
    if (!chosen) continue;
    const verdict = measured ? toneForVerdict(measured.verdict) : { tone: "up" as Mc2Tone, mark: "+" };
    const title = chosen.title ?? (chosen.kind === "measured" ? "A shipped change was measured" : "A change went live");
    movers.push({
      siteId,
      siteName: chosen.siteName,
      mark: verdict.mark,
      tone: verdict.tone,
      at: chosen.at,
      why: measured
        ? measured.windowDays !== null
          ? `${title} — measured after ${measured.windowDays} days: ${measured.verdict ?? "no clear change"}.`
          : `${title} — measured: ${measured.verdict ?? "no clear change"}.`
        : `${title} — now live. Measurement starts in the following days.`,
    });
  }
  return movers.sort((left, right) => right.at.localeCompare(left.at)).slice(0, 5);
}

export function recapCounts(recap: OperatorRecapV1 | null) {
  return {
    deployed: recap?.totals.deployed ?? 0,
    rolledBack: recap?.totals.rolledBack ?? 0,
    failed: recap?.totals.failed ?? 0,
    measured: recap?.totals.measured ?? 0,
    improved: recap?.totals.measuredImproved ?? 0,
    audits: recap?.totals.auditsCompleted ?? 0,
    awaiting: recap?.totals.awaitingApproval ?? 0,
  };
}

/** A compact sparkline from real buckets. Returns null when there is nothing worth drawing. */
export function sparkline(values: readonly number[], width = 84, height = 28): string | null {
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  return values
    .map((value, index) => `${((index * width) / (values.length - 1)).toFixed(1)},${(height - 3 - ((value - min) / range) * (height - 6)).toFixed(1)}`)
    .join(" ");
}

/** Counts per day from the journal, oldest first, for the last `days` days. */
export function dailyCounts(journal: readonly OperatorRecapEntry[], now: number, days: number, kinds?: readonly RecapEntryKind[]): number[] {
  const buckets = new Array<number>(days).fill(0);
  for (const entry of journal) {
    if (kinds && !kinds.includes(entry.kind)) continue;
    const age = Math.floor((now - new Date(entry.at).getTime()) / 86_400_000);
    if (age < 0 || age >= days) continue;
    const index = days - 1 - age;
    buckets[index] = (buckets[index] ?? 0) + 1;
  }
  return buckets;
}

export function siteById(overview: OperatorOverview | null, siteId: SiteId | null): OperatorSiteSnapshot | null {
  if (!overview || !siteId) return null;
  return overview.sites.find((site) => site.id === siteId) ?? null;
}


/** One sentence for an inbox row, in the viewer's language, from stored fields only. */
export function inboxSubtitle(item: InboxItem, copy: import("./copy").MC2Copy, ops: import("../copy").OperatorCopy): string {
  switch (item.kind) {
    case "stopped":
      return item.card.errorMessage ?? ops.pipeline.noErrorMessage;
    case "approve":
      return item.card.batch?.eligible ? copy.inbox.approveChecksPassed : copy.inbox.approveNeedsDecision;
    case "audit":
      return item.pending > 0 ? ops.today.auditReady(item.pending) : ops.audit.openReport;
    case "report":
      return item.report.quiet
        ? ops.reports.quiet
        : `${item.report.totals.shipped} ${ops.reports.columns.shipped.toLowerCase()} · ${item.report.totals.measured} ${ops.reports.columns.measured.toLowerCase()}`;
    case "setup": {
      const waiting = item.site.awaitingApproval + item.site.pendingRecommendations;
      return waiting > 0 ? copy.inbox.changesWaiting(waiting) : copy.inbox.setupStopped;
    }
    case "incident":
      return `${copy.inbox.severity[item.incident.severity] ?? item.incident.severity} · ${copy.inbox.issue[item.incident.issueType] ?? item.incident.issueType}`;
    default:
      return "";
  }
}
